// src/pages/ActivityLog.js - ENGLISH VERSION
import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  Card,
  CardContent,
  MenuItem,
  alpha,
  useTheme,
  useMediaQuery,
  Pagination,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Fade,
  Divider,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  Search as SearchIcon,
  History as HistoryIcon,
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  FileUpload as FileUploadIcon,
  Assignment as AssignmentIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  FilterList as FilterListIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Security as SecurityIcon,
  GppGood as EvidenceIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale'; // Changed locale to English
import activityLogService from '../services/activityLogService';

// Activity types with icons and colors
const ACTIVITY_TYPES = {
  create: {
    icon: PersonAddIcon,
    color: '#2e7d32',
    label: 'Creation',
    bgColor: '#e8f5e9'
  },
  edit: {
    icon: EditIcon,
    color: '#0288d1',
    label: 'Edit',
    bgColor: '#e3f2fd'
  },
  delete: {
    icon: DeleteIcon,
    color: '#d32f2f',
    label: 'Deletion',
    bgColor: '#ffebee'
  },
  complete: {
    icon: CheckCircleIcon,
    color: '#7b1fa2',
    label: 'Completed',
    bgColor: '#f3e5f5'
  },
  upload: {
    icon: FileUploadIcon,
    color: '#ed6c02',
    label: 'Upload',
    bgColor: '#fff3e0'
  },
  security_info: {
    icon: SecurityIcon,
    color: '#1976d2',
    label: 'Security Info',
    bgColor: '#e3f2fd'
  },
  security_warning: {
    icon: SecurityIcon,
    color: '#ed6c02',
    label: 'Security Warning',
    bgColor: '#fff3e0'
  },
  security_critical: {
    icon: SecurityIcon,
    color: '#d32f2f',
    label: 'Security Critical',
    bgColor: '#ffebee'
  },
};

// System modules
const MODULES = {
  project: { icon: AssignmentIcon, label: 'Projects' },
  client: { icon: BusinessIcon, label: 'Clients' },
  user: { icon: PersonIcon, label: 'Users' },
  task: { icon: CheckCircleIcon, label: 'Tasks' },
  document: { icon: FileUploadIcon, label: 'Documents' },
  security: { icon: SecurityIcon, label: 'Security' },
};

const tryParseJsonString = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed || (trimmed[0] !== '{' && trimmed[0] !== '[')) return null;
  try {
    return JSON.parse(trimmed);
  } catch (e) {
    return null;
  }
};

const formatObjectDetails = (obj) => {
  if (!obj || typeof obj !== 'object') return '';

  if (obj.from_status || obj.to_status) {
    const from = obj.from_status || '-';
    const to = obj.to_status || '-';
    const number = obj.invoice_number ? ` (${obj.invoice_number})` : '';
    const prefix = obj.automatic ? 'Automatic status' : 'Status';
    return `${prefix}: ${from} -> ${to}${number}`;
  }

  return Object.entries(obj)
    .map(([key, value]) => `${key}: ${value === null || value === undefined ? '-' : String(value)}`)
    .join(' | ');
};

const formatActivityDetails = (rawDetails) => {
  if (rawDetails === null || rawDetails === undefined) return '';
  if (typeof rawDetails === 'object') return formatObjectDetails(rawDetails);

  const detailsText = String(rawDetails);
  const parsed = tryParseJsonString(detailsText);
  if (parsed && typeof parsed === 'object') return formatObjectDetails(parsed);

  return detailsText;
};

export default function ActivityLog() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [securityAvailable, setSecurityAvailable] = useState(null);
  const [exportingEvidence, setExportingEvidence] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterModule, setFilterModule] = useState('all');
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;

  // Load activities on mount
  useEffect(() => {
    loadActivities();
  }, []);

  const loadActivities = async () => {
    try {
      setLoading(true);
      setError('');
      
      const [response, securityResponse] = await Promise.all([
        activityLogService.getActivityLogs({ limit: 250 }),
        activityLogService.getSecurityEvents({ limit: 500 }).catch((securityError) => {
          if (securityError?.response?.status === 404) {
            return { success: true, available: false, count: 0, total: 0, events: [] };
          }
          throw securityError;
        })
      ]);
      
      const formattedActivities = response.activities.map(a => ({
        id: a.id,
        type: a.type,
        module: a.module,
        user: a.user || 'Unknown User',
        action: a.action,
        target: a.target || '',
        details: formatActivityDetails(a.details),
        timestamp: new Date(a.timestamp),
        source: 'activity',
        ip_address: a.ip_address || '',
        event_type: '',
        severity: '',
      }));

      const securityActivities = (securityResponse.events || []).map((event) => ({
        id: `security-${event.id}`,
        type: `security_${event.severity || 'info'}`,
        module: 'security',
        user: event.user || 'Unknown account',
        action: String(event.event_type || 'security_event').replace(/_/g, ' '),
        target: event.subject || 'Security control',
        details: formatActivityDetails(event.details_json),
        timestamp: new Date(event.created_at),
        source: 'security',
        ip_address: event.ip_address || '',
        event_type: event.event_type || '',
        severity: event.severity || 'info',
      }));

      setSecurityAvailable(securityResponse.available !== false);
      setActivities([...formattedActivities, ...securityActivities]
        .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime()));
      setSuccess('✅ Activities loaded successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error loading activities:', error);
      setError('Error loading activities. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filteredActivities = useMemo(() => {
    return activities.filter((activity) => {
      const matchSearch = 
        String(activity.user || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(activity.target || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(activity.details || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(activity.event_type || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchType = filterType === 'all' || activity.type === filterType;
      const matchModule = filterModule === 'all' || activity.module === filterModule;

      return matchSearch && matchType && matchModule;
    });
  }, [activities, searchTerm, filterType, filterModule]);

  const paginatedActivities = useMemo(() => {
    const startIndex = (page - 1) * itemsPerPage;
    return filteredActivities.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredActivities, page]);

  const totalPages = Math.ceil(filteredActivities.length / itemsPerPage);

  const stats = useMemo(() => ({
    total: activities.length,
    today: activities.filter(a => {
      const now = new Date();
      return format(a.timestamp, 'yyyy-MM-dd') === format(now, 'yyyy-MM-dd');
    }).length,
    thisWeek: activities.filter(a => {
      const now = new Date();
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      return a.timestamp >= weekAgo;
    }).length,
    security: activities.filter((activity) => activity.source === 'security').length,
    critical: activities.filter((activity) => activity.severity === 'critical').length,
  }), [activities]);

  // Export to CSV
  const handleExport = () => {
    const escapeCsv = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const csvContent = [
      ['Date', 'Source', 'User', 'Action', 'Module', 'Target', 'Event Type', 'Severity', 'IP Address', 'Details'],
      ...filteredActivities.map(a => [
        format(a.timestamp, 'MM/dd/yyyy HH:mm', { locale: enUS }),
        a.source,
        a.user,
        ACTIVITY_TYPES[a.type]?.label || a.type,
        MODULES[a.module]?.label || a.module,
        a.target,
        a.event_type,
        a.severity,
        a.ip_address,
        a.details
      ])
    ].map((row) => row.map(escapeCsv).join(',')).join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const objectUrl = URL.createObjectURL(blob);
    link.href = objectUrl;
    link.download = `activity_log_${format(new Date(), 'yyyyMMdd')}.csv`;
    link.click();
    URL.revokeObjectURL(objectUrl);
    
    setSuccess('📄 CSV exported successfully');
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleSecurityEvidenceExport = async () => {
    try {
      setExportingEvidence(true);
      setError('');
      const response = await activityLogService.getSecurityEvidence();
      const payload = JSON.stringify(response.evidence, null, 2);
      const blob = new Blob([payload], { type: 'application/json;charset=utf-8;' });
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `security_evidence_${format(new Date(), 'yyyyMMdd_HHmm')}.json`;
      link.click();
      URL.revokeObjectURL(objectUrl);
      setSuccess('Security evidence exported successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (exportError) {
      console.error('Error exporting security evidence:', exportError);
      setError('Unable to export security evidence. Verify that the security migration is installed.');
    } finally {
      setExportingEvidence(false);
    }
  };

  const StatCard = ({ title, value, color, icon: Icon }) => (
    <Fade in timeout={500}>
      <Card 
        elevation={0} 
        sx={{ 
          border: '1px solid', 
          borderColor: 'divider', 
          borderRadius: 3,
          height: '100%',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: 6,
            transform: 'translateY(-4px)',
            borderColor: color
          }
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ flex: 1 }}>
              <Typography 
                variant="caption" 
                color="text.secondary" 
                sx={{ 
                  mb: 0.5, 
                  fontWeight: 600, 
                  textTransform: 'uppercase', 
                  letterSpacing: 0.5 
                }}
              >
                {title}
              </Typography>
              <Typography 
                variant="h3" 
                sx={{ 
                  fontWeight: 700, 
                  color: color,
                  mb: 0.5
                }}
              >
                {value}
              </Typography>
            </Box>
            <Avatar
              sx={{
                bgcolor: alpha(color, 0.1),
                width: 56,
                height: 56,
              }}
            >
              <Icon sx={{ fontSize: 28, color }} />
            </Avatar>
          </Box>
        </CardContent>
      </Card>
    </Fade>
  );

  const ActivityCard = ({ activity }) => {
    const activityType = ACTIVITY_TYPES[activity.type] || ACTIVITY_TYPES.create;
    const module = MODULES[activity.module] || MODULES.project;
    const Icon = activityType.icon;
    const ModuleIcon = module.icon;

    return (
      <Fade in timeout={300}>
        <Card
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 3,
            transition: 'all 0.3s ease',
            '@media (hover: hover)': {
              '&:hover': {
                boxShadow: 6,
                transform: 'translateY(-4px)',
                borderColor: activityType.color,
              },
            },
          }}
        >
          <CardContent sx={{ p: { xs: 1.5, sm: 2.25, md: 3 } }}>
            <Box sx={{ display: 'flex', gap: { xs: 1.25, sm: 2 } }}>
              <Avatar
                sx={{
                  bgcolor: alpha(activityType.color, 0.15),
                  width: { xs: 40, sm: 56 },
                  height: { xs: 40, sm: 56 },
                  flexShrink: 0,
                }}
              >
                <Icon
                  sx={{
                    color: activityType.color,
                    fontSize: { xs: 21, sm: 28 },
                  }}
                />
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 0.5, sm: 1 }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'flex-start' }, mb: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {activity.user}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {activity.action} <strong style={{ color: activityType.color }}>{activity.target}</strong>
                    </Typography>
                  </Box>
                  <Typography 
                    variant="caption" 
                    color="text.secondary" 
                    sx={{ 
                      whiteSpace: { xs: 'normal', sm: 'nowrap' },
                      ml: { xs: 0, sm: 2 },
                      fontWeight: 500
                    }}
                  >
                    {format(activity.timestamp, 'MMM dd yyyy, HH:mm', { locale: enUS })}
                  </Typography>
                </Box>
                
                {activity.details && (
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      mb: 2, 
                      fontSize: '0.875rem',
                      fontStyle: 'italic'
                    }}
                  >
                    "{activity.details}"
                  </Typography>
                )}

                <Divider sx={{ my: 1.5 }} />

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    icon={<ModuleIcon sx={{ fontSize: 16 }} />}
                    label={module.label}
                    size="small"
                    variant="outlined"
                    sx={{ 
                      fontSize: '0.75rem',
                      fontWeight: 600
                    }}
                  />
                  <Chip
                    label={activityType.label}
                    size="small"
                    sx={{
                      bgcolor: alpha(activityType.color, 0.1),
                      color: activityType.color,
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      borderRadius: 2
                    }}
                  />
                  {activity.severity && (
                    <Chip
                      label={`Severity: ${activity.severity}`}
                      size="small"
                      color={activity.severity === 'critical' ? 'error' : (activity.severity === 'warning' ? 'warning' : 'info')}
                      variant="outlined"
                      sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                    />
                  )}
                  {activity.ip_address && (
                    <Chip
                      label={`IP: ${activity.ip_address}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontFamily: 'monospace', fontSize: '0.72rem' }}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Fade>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', gap: 2 }}>
        <CircularProgress size={60} />
        <Typography variant="body2" color="text.secondary">
          Loading activity log...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      <Fade in timeout={500}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.35, md: 1.6 },
            mb: 1.5,
            borderRadius: 2.5,
            background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
            color: 'white',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: -50,
              right: -50,
              width: 200,
              height: 200,
              borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,0.1)',
            }}
          />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.25, position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1 }}>
              <Box sx={{ bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 2, p: 0.8, display: 'flex' }}>
                <HistoryIcon sx={{ fontSize: 22 }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800, fontSize: { xs: '1.05rem', md: '1.3rem' } }}>
                  Activity Log
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Complete record of system actions and security events
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Export security evidence">
                <span>
                  <IconButton
                  onClick={handleSecurityEvidenceExport}
                  disabled={exportingEvidence}
                  aria-label="Export security evidence"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                    }}
                  >
                    {exportingEvidence ? <CircularProgress size={22} color="inherit" /> : <EvidenceIcon />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Refresh">
                <IconButton
                  onClick={loadActivities}
                  disabled={loading}
                  aria-label="Refresh activity log"
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export to CSV">
                <IconButton
                  onClick={handleExport}
                  disabled={filteredActivities.length === 0}
                  aria-label="Export activity log to CSV"
                  sx={{
                    bgcolor: 'white',
                    color: 'primary.main',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                  }}
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Paper>
      </Fade>

      {/* Alerts */}
      {error && (
        <Fade in>
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        </Fade>
      )}
      {success && (
        <Fade in>
          <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        </Fade>
      )}
      {securityAvailable === false && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
          Security events are unavailable. Deploy the current Activity Log backend routes and run <strong>scripts/security_hardening_foundation.sql</strong> on the production database.
        </Alert>
      )}

      {/* Stats */}
      <Grid container spacing={1.2} sx={{ mb: 1.5 }}>
        <Grid item xs={6} md={3}>
          <StatCard title="Total Activities" value={stats.total} color="#1976d2" icon={HistoryIcon} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard title="Today" value={stats.today} color="#2e7d32" icon={CheckCircleIcon} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard title="Security Events" value={stats.security} color="#ed6c02" icon={SecurityIcon} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard title="Critical Events" value={stats.critical} color="#d32f2f" icon={InfoIcon} />
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper elevation={0} sx={{ p: 1.3, mb: 1.5, borderRadius: 2.5, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <FilterListIcon sx={{ color: 'primary.main', fontSize: 24 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            Filters & Search
          </Typography>
          <Chip 
            label={`${filteredActivities.length} results`} 
            size="small" 
            color="primary" 
            sx={{ ml: 1 }} 
          />
        </Box>
        <Grid container spacing={1.2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by user, event, target, or details..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="primary" />
                  </InputAdornment>
                ),
              }}
              sx={{ borderRadius: 2 }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              select
              fullWidth
              size="small"
              label="Action Type"
              value={filterType}
              onChange={(e) => {
                setFilterType(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="all">All</MenuItem>
              {Object.entries(ACTIVITY_TYPES).map(([key, value]) => (
                <MenuItem key={key} value={key}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: value.color }} />
                    {value.label}
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              select
              fullWidth
              size="small"
              label="Module"
              value={filterModule}
              onChange={(e) => {
                setFilterModule(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="all">All</MenuItem>
              {Object.entries(MODULES).map(([key, value]) => {
                const Icon = value.icon;
                return (
                  <MenuItem key={key} value={key}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Icon sx={{ fontSize: 18 }} />
                      {value.label}
                    </Box>
                  </MenuItem>
                );
              })}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Activity List */}
      {paginatedActivities.length > 0 ? (
        <Grid container spacing={1.2}>
          {paginatedActivities.map((activity) => (
            <Grid item xs={12} key={activity.id}>
              <ActivityCard activity={activity} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Paper 
          elevation={0} 
          sx={{ 
            p: { xs: 3, sm: 6, md: 8 },
            textAlign: 'center', 
            border: '1px solid', 
            borderColor: 'divider', 
            borderRadius: 3 
          }}
        >
          <Avatar
            sx={{
              width: 80,
              height: 80,
              bgcolor: alpha('#1976d2', 0.1),
              margin: '0 auto',
              mb: 2
            }}
          >
            <HistoryIcon sx={{ fontSize: 40, color: '#1976d2' }} />
          </Avatar>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
            No activities found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchTerm || filterType !== 'all' || filterModule !== 'all'
              ? 'Try adjusting the search filters'
              : 'No activities registered in the system yet'}
          </Typography>
        </Paper>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
            size={isMobile ? 'medium' : 'large'}
            showFirstButton={!isMobile}
            showLastButton={!isMobile}
            sx={{
              '& .MuiPaginationItem-root': {
                borderRadius: 2,
                fontWeight: 600
              }
            }}
          />
        </Box>
      )}
    </Box>
  );
}
